/**
 * useAnalysisEvents — subscribes to the backend SSE analysis event stream.
 *
 * The stream endpoint is GET /api/analyses/stream (auth via ?token= query param
 * because EventSource cannot set headers — same pattern as investigation stream).
 *
 * Event types from the contract:
 *   analysis_started    {analysis_id, issue_id, model, started_at}
 *   analysis_progress   {analysis_id, current_round, current_tool}
 *   analysis_completed  {analysis_id, issue_id, severity, completed_at, cost_usd}
 *   analysis_failed     {analysis_id, issue_id, error_message}
 *   analysis_cancelled  {analysis_id, issue_id, cancelled_at}
 *   heartbeat           {ts}
 *
 * Reconnects with exponential back-off (initial 1s, doubles each attempt, cap 30s).
 * When the tab becomes hidden the EventSource is closed to avoid draining the
 * server's SSE connection pool on idle tabs. When the tab regains focus the
 * stream is reopened; back-off state is preserved across hide/show so we don't
 * reset to 1s on every focus.
 * Cleans up automatically on unmount.
 */

import { useEffect, useRef, useState } from 'react';

import { analyses } from '@/helpers/ApiHelper';

// ---------------------------------------------------------------------------
// Event payload types — mirror the wire shapes from the SSE contract
// ---------------------------------------------------------------------------

export interface AnalysisStartedData {
  analysis_id: string;
  issue_id: string;
  model: string;
  started_at: string;
}

export interface AnalysisProgressData {
  analysis_id: string;
  issue_id: string;
  current_round: number;
  current_tool: string | null;
}

export interface AnalysisCompletedData {
  analysis_id: string;
  issue_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | null;
  completed_at: string;
  cost_usd: number;
}

export interface AnalysisFailedData {
  analysis_id: string;
  issue_id: string;
  error_message: string;
}

export interface AnalysisCancelledData {
  analysis_id: string;
  issue_id: string;
  cancelled_at: string;
}

export interface HeartbeatData {
  ts: string;
}

export type AnalysisSSEEvent =
  | { event: 'analysis_started';    data: AnalysisStartedData }
  | { event: 'analysis_progress';   data: AnalysisProgressData }
  | { event: 'analysis_completed';  data: AnalysisCompletedData }
  | { event: 'analysis_failed';     data: AnalysisFailedData }
  | { event: 'analysis_cancelled';  data: AnalysisCancelledData }
  | { event: 'heartbeat';           data: HeartbeatData };

// ---------------------------------------------------------------------------
// Hook options + return shape
// ---------------------------------------------------------------------------

export type AnalysisEventConnectionState = 'connecting' | 'open' | 'reconnecting';

export interface UseAnalysisEventsOptions {
  /** If provided, only events for this issue are received. */
  issueId?: string;
  /** Called once per inbound event (excluding heartbeat — still passed but easy to ignore). */
  onEvent: (event: AnalysisSSEEvent) => void;
  /** Whether the hook is active. Defaults to true. Pass false to disable. */
  enabled?: boolean;
}

// Back-off constants
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useAnalysisEvents({
  issueId,
  onEvent,
  enabled = true,
}: UseAnalysisEventsOptions): AnalysisEventConnectionState {
  const [connectionState, setConnectionState] =
    useState<AnalysisEventConnectionState>('connecting');

  // Keep a stable ref to the callback so the effect doesn't re-run when
  // the parent re-renders with a new arrow function.
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let destroyed = false;
    let reconnectTimerId: ReturnType<typeof setTimeout> | null = null;

    // Shared back-off counter — persists across hide/show cycles so a
    // frequently hidden tab doesn't spam the server with 1s reconnects.
    let attempt = 0;

    function backoffDelay(): number {
      const delay = Math.min(INITIAL_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      return delay;
    }

    function connect(): void {
      if (destroyed) return;
      setConnectionState(attempt === 0 ? 'connecting' : 'reconnecting');

      const url = analyses.streamUrl(issueId);
      es = new EventSource(url);

      es.onopen = () => {
        if (destroyed) { es?.close(); return; }
        attempt = 0; // reset back-off on successful connection
        setConnectionState('open');
      };

      // Named event listeners for each event type
      const eventTypes: AnalysisSSEEvent['event'][] = [
        'analysis_started',
        'analysis_progress',
        'analysis_completed',
        'analysis_failed',
        'analysis_cancelled',
        'heartbeat',
      ];

      for (const eventType of eventTypes) {
        es.addEventListener(eventType, (rawEvt: Event) => {
          if (destroyed) return;
          const msgEvent = rawEvt as MessageEvent<string>;
          let parsed: unknown;
          try {
            parsed = JSON.parse(msgEvent.data);
          } catch {
            return; // malformed frame — skip
          }
          // Trust wire shape; narrow by event name for consumers.
          onEventRef.current({
            event: eventType,
            data: parsed,
          } as AnalysisSSEEvent);
        });
      }

      es.onerror = () => {
        if (destroyed) return;
        es?.close();
        es = null;
        setConnectionState('reconnecting');
        const delay = backoffDelay();
        reconnectTimerId = setTimeout(connect, delay);
      };
    }

    function disconnect(): void {
      if (reconnectTimerId !== null) {
        clearTimeout(reconnectTimerId);
        reconnectTimerId = null;
      }
      es?.close();
      es = null;
    }

    // ── Page visibility handler ──────────────────────────────────────────
    // Close the EventSource when the tab is hidden to avoid draining the
    // server's SSE connection pool on idle tabs.
    // Re-open when the tab regains visibility (back-off state is kept so
    // we don't reset to 1s on every focus event).
    function handleVisibilityChange(): void {
      if (destroyed) return;
      if (document.hidden) {
        disconnect();
        setConnectionState('reconnecting');
      } else {
        // Tab is visible again — reconnect.
        connect();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Only connect immediately if the tab is visible.
    if (!document.hidden) {
      connect();
    }

    return () => {
      destroyed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnect();
    };
  }, [issueId, enabled]);

  return connectionState;
}
